import { AbilityBuilder, PureAbility } from '@casl/ability';
import { C360Ability } from './abilities';
import { Product } from './utils/product';
import { Permission } from './permissions';
import { Agency } from './agencies';

/**
 * Defines ability based on user type and permissions.
 *
 * @param {import('./types').User} user
 * @returns {PureAbility}
 */
export default function defineAbilityFor(user) {
  if (!user) {
    throw new Error('You must provide user for defineAbilityFor function');
  }

  const { can, build } = new AbilityBuilder(PureAbility);

  const products = user.products || [];
  const permissions = user.permissions || [];

  // products abilities
  // show analytics product only for SU or users with Analytics in the token
  if (products.includes(Product.Analytics)) {
    can(C360Ability.See, Product.Analytics);
    can(C360Ability.Interact, Product.Analytics);
  }

  // show media planner product only for SU or users with Media Planner in the token
  if (products.includes(Product.MediaPlanner)) {
    can(C360Ability.See, Product.MediaPlanner);
    can(C360Ability.Interact, Product.MediaPlanner);
  }

  // show CompulseOMS product only for users with SSWebApp in the token
  // always hide SSWebApp from SU
  if (products.includes(Product.SSWebApp)) {
    can(C360Ability.See, Product.SSWebApp);
    can(C360Ability.Interact, Product.SSWebApp);
  }

  // show Help Center for all users without HIDE_HELP_CENTER permission in the token
  if (!permissions.includes(Permission.HideHelpCenter)) {
    can(C360Ability.See, Product.HelpCenter);
    can(C360Ability.Interact, Product.HelpCenter);
    can(C360Ability.SeeEmailInTheFooter);
  }

  // show Instant IO product for users with Instant IO in the token if active agency name is not Sinclair Broadcast Group
  if (products.includes(Product.InstantIO) && user.activeAgencyName !== Agency.Sinclair) {
    can(C360Ability.See, Product.InstantIO);
    can(C360Ability.Interact, Product.InstantIO);
  }

  // show Orders product for all users with Orders in the token
  if (products.includes(Product.Orders)) {
    can(C360Ability.See, Product.Orders);
  }

  // allow to click on Orders product only for users with specific permission
  if (user.isSuperUser || permissions.includes(Permission.ShowOrdersSummaryView)) {
    can(C360Ability.Interact, Product.Orders);
  }

  if (user.tenantsCount && user.tenantsCount > 1) {
    can(C360Ability.AlwaysSeeSwitchProductDrawer);
  }

  // support chat bot permissions
  if (user.isSuperUser || permissions.includes(Permission.CanInteractWithChatbot)) {
    can(C360Ability.Interact, 'chatbot');
  }

  // support chat bot statistics permissions
  if (user.isSuperUser || permissions.includes(Permission.CanInteractWithStatisticChatbot)) {
    can(C360Ability.Interact, 'statistic-chatbot');
  }

  return build();
}
